import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Upload, CreditCard, Search, X, ExternalLink, Plus, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { usePayments, useFinancialSummary, useStudentPaymentSummaries } from '../../hooks/usePayments';
import { invalidateQueries } from '../../lib/queryClient';
import BulkPaymentUploadModal from '../payments/BulkPaymentUploadModal';
import { FlagBadge, ResolvePaymentDialog } from '../payments/ResolvePaymentDialog';
import AddPaymentDialog from '../students/AddPaymentDialog';
import { api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Shared badge components
// ---------------------------------------------------------------------------
function StatusBadge({ status }) {
  const { t } = useTranslation();
  const styles = {
    COMPLETED:  'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    PENDING:    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
    FAILED:     'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    CANCELLED:  'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    PROCESSING: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    REFUNDED:   'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    ERROR:      'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 line-through opacity-60',
  };
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${styles[status] || ''}`}>
      {t(`payments.status.${status}`, status)}
    </span>
  );
}

function TypeBadge({ type }) {
  const { t } = useTranslation();
  const styles = {
    DEPOSIT:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    BALANCE:    'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
    REFUND:     'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    ADJUSTMENT: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    SCHOLARSHIP:'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  };
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${styles[type] || ''}`}>
      {t(`payments.type.${type}`, type)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Highlight matched search term within a string
// ---------------------------------------------------------------------------
function Highlight({ text, query }) {
  if (!text) return null;
  if (!query.trim()) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-700/60 text-inherit rounded-sm px-0.5">{part}</mark>
          : part
      )}
    </>
  );
}

function matchesQuery(text, query) {
  if (!text || !query.trim()) return false;
  return text.toLowerCase().includes(query.toLowerCase());
}

// ---------------------------------------------------------------------------
// Student picker dialog (for Add Payment)
// ---------------------------------------------------------------------------
function StudentPickerDialog({ open, onClose, onSelect }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.students.getAll({ search: query, page_size: 8 });
        setResults(data.results || []);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-lg">{t('payments.overview.selectStudent')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            autoFocus
            className="pl-9"
            placeholder={t('payments.overview.searchStudent')}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t('payments.overview.loading')}
            </div>
          ) : results.length > 0 ? (
            results.map(student => {
              const showParentName = matchesQuery(student.parent_name, query);
              const showParentEmail = matchesQuery(student.parent_email, query);
              return (
                <button
                  key={student.uuid}
                  onClick={() => onSelect(student)}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted/70 transition-colors"
                >
                  <div className="font-medium text-sm">
                    <Highlight text={student.full_name} query={query} />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <Highlight text={student.student_id} query={query} />
                    {student.course_name ? ` · ${student.course_name}` : ''}
                  </div>
                  {(showParentName || showParentEmail) && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {showParentName && <Highlight text={student.parent_name} query={query} />}
                      {showParentName && showParentEmail && ' · '}
                      {showParentEmail && <Highlight text={student.parent_email} query={query} />}
                    </div>
                  )}
                </button>
              );
            })
          ) : query.trim() ? (
            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('payments.overview.noStudents')}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------
function KpiCard({ label, value, sub, colour, action }) {
  const colours = {
    green: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
    blue:  'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    red:   'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
    slate: 'bg-slate-50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300',
  };
  return (
    <div className={`rounded-xl p-4 flex items-center justify-between gap-2 ${colours[colour] || colours.slate}`}>
      <div className="flex flex-col gap-1 min-w-0">
        <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
        {sub && <div className="text-xs opacity-70">{sub}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breakdown tables
// ---------------------------------------------------------------------------
const PAYMENT_TYPES = ['DEPOSIT', 'BALANCE', 'SCHOLARSHIP', 'REFUND', 'ADJUSTMENT'];
const PAYMENT_METHODS = ['STRIPE', 'BANK_TRANSFER', 'CARD', 'CASH', 'CHEQUE'];

function BreakdownTables({ financial, isLoading }) {
  const { t } = useTranslation();
  const formatCurrency = (v) =>
    `€${(parseFloat(v) || 0).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-card border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b font-medium text-sm">
          {t('payments.overview.byType')}
        </div>
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-8 bg-muted/40 rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              {PAYMENT_TYPES.filter(type => financial?.by_payment_type?.[type]).map(type => {
                const d = financial.by_payment_type[type];
                return (
                  <tr key={type} className="hover:bg-muted/30">
                    <td className="px-4 py-2.5"><TypeBadge type={type} /></td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">
                      {d.count}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">
                      {formatCurrency(d.total)}
                    </td>
                  </tr>
                );
              })}
              {(!financial?.by_payment_type || Object.keys(financial.by_payment_type).length === 0) && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                    {t('payments.overview.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-card border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b font-medium text-sm">
          {t('payments.overview.byMethod')}
        </div>
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-8 bg-muted/40 rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              {PAYMENT_METHODS.filter(method => financial?.by_payment_method?.[method]).map(method => {
                const d = financial.by_payment_method[method];
                return (
                  <tr key={method} className="hover:bg-muted/30">
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                      {t(`payments.method.${method}`)}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">
                      {d.count}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">
                      {formatCurrency(d.total)}
                    </td>
                  </tr>
                );
              })}
              {(!financial?.by_payment_method || Object.keys(financial.by_payment_method).length === 0) && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                    {t('payments.overview.noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function PaymentsPage() {
  const { t } = useTranslation();

  // Upload / add payment
  const [uploadOpen, setUploadOpen] = useState(false);
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);
  const [addPaymentStudent, setAddPaymentStudent] = useState(null);

  // Flagged section
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [resolving, setResolving] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Data
  const { data: summary, isLoading: summaryLoading } = useStudentPaymentSummaries();
  const { data: financial, isLoading: financialLoading } = useFinancialSummary();

  // Flagged count (for KPI card — not affected by show_errors)
  const { data: flaggedCountData } = usePayments({ has_flag: 'true' });
  const flaggedCount = (flaggedCountData?.results ?? flaggedCountData ?? []).length;

  // Flagged table (respects search + show_errors)
  const isSearching = debouncedSearch.trim().length > 0;
  const flaggedQueryParams = {
    ...(isSearching ? { search: debouncedSearch } : { has_flag: 'true' }),
    show_errors: showErrors ? 'true' : 'false',
  };
  const { data: flaggedTableData, isLoading: flaggedLoading, isError: flaggedError } = usePayments(flaggedQueryParams);
  const flaggedPayments = flaggedTableData?.results ?? flaggedTableData ?? [];

  const totals = summary?.summary_totals;
  const formatCurrency = (v) =>
    `€${(parseFloat(v) || 0).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IE', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const formatAmount = (a) => `€${parseFloat(a || 0).toFixed(2)}`;

  const tableTitle = isSearching
    ? t('payments.page.searchResults', { term: debouncedSearch, count: flaggedPayments.length })
    : t('payments.page.flaggedCount', { count: flaggedPayments.length });

  const handleStudentSelected = (student) => {
    setStudentPickerOpen(false);
    setAddPaymentStudent(student);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('payments.title')}</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setUploadOpen(true)} className="gap-2">
            <Upload className="w-4 h-4" />
            {t('payments.page.uploadCsv')}
          </Button>
          <Button onClick={() => setStudentPickerOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
            <Plus className="w-4 h-4" />
            {t('payments.overview.addPayment')}
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-xl bg-muted/40 h-24 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard
            label={t('payments.overview.totalCollected')}
            value={formatCurrency(totals?.total_paid)}
            colour="green"
          />
          <KpiCard
            label={t('payments.overview.totalOutstanding')}
            value={formatCurrency(totals?.total_outstanding)}
            colour={totals?.total_outstanding > 0 ? 'amber' : 'slate'}
            action={totals?.total_outstanding > 0 ? (
              <Link
                to="/students?paymentStage=outstanding"
                className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 transition-colors whitespace-nowrap"
              >
                {t('payments.overview.viewStudents')}
              </Link>
            ) : null}
          />
          <KpiCard
            label={t('payments.overview.collectionRate')}
            value={`${totals?.collection_rate ?? 0}%`}
            colour="blue"
          />
          <KpiCard
            label={t('payments.overview.flagged')}
            value={flaggedCount}
            colour={flaggedCount > 0 ? 'red' : 'slate'}
          />
        </div>
      )}

      {/* Flagged payments — only shown when flagged records exist */}
      {flaggedCount > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-semibold text-sm text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              {t('payments.page.flaggedTitle')}
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <Input
                  className="pl-9 pr-8 h-8 text-sm"
                  placeholder={t('payments.page.searchPlaceholder')}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setSearchInput('')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowErrors(v => !v)}
                className={showErrors ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800' : ''}
              >
                {showErrors ? t('payments.page.hideErrors') : t('payments.page.showErrors')}
              </Button>
            </div>
          </div>

          <div className="bg-card border rounded-xl overflow-hidden">
            {flaggedLoading ? (
              <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              </div>
            ) : flaggedError ? (
              <div className="py-10 text-center text-sm text-red-500">
                {t('payments.page.error')}
              </div>
            ) : flaggedPayments.length === 0 ? (
              <div className="py-10 text-center">
                <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('payments.page.noResults')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="px-4 py-2 border-b text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {tableTitle}
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                      <th className="px-4 py-3 font-medium">{t('payments.page.col.date')}</th>
                      <th className="px-4 py-3 font-medium">{t('payments.page.col.student')}</th>
                      <th className="px-4 py-3 font-medium text-right">{t('payments.page.col.amount')}</th>
                      <th className="px-4 py-3 font-medium">{t('payments.page.col.type')}</th>
                      <th className="px-4 py-3 font-medium">{t('payments.page.col.method')}</th>
                      <th className="px-4 py-3 font-medium">{t('payments.page.col.status')}</th>
                      <th className="px-4 py-3 font-medium">{t('payments.page.col.flag')}</th>
                      <th className="px-4 py-3 font-medium">{t('payments.page.col.reference')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {flaggedPayments.map((payment) => {
                      const isFlagged = !!payment.import_flag;
                      return (
                        <tr
                          key={payment.uuid}
                          className={`transition-colors ${isFlagged ? 'cursor-pointer hover:bg-amber-50/60 dark:hover:bg-amber-900/10' : 'hover:bg-muted/30'}`}
                          onClick={isFlagged ? () => setResolving(payment) : undefined}
                        >
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {formatDate(payment.payment_date)}
                          </td>
                          <td className="px-4 py-3">
                            {payment.student_uuid ? (
                              <div className="space-y-1">
                                <Link
                                  to={`/students/${payment.student_uuid}`}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                                  onClick={e => e.stopPropagation()}
                                >
                                  {payment.student_name}
                                  <ExternalLink className="w-3 h-3 shrink-0" />
                                </Link>
                                <div className="text-xs text-gray-400 pl-0.5">{payment.student_id_display}</div>
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-sm text-orange-600 dark:text-orange-400 font-medium">
                                {t('payments.page.unmatched')}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 tabular-nums font-medium text-right whitespace-nowrap">
                            {formatAmount(payment.display_amount ?? payment.amount)}
                          </td>
                          <td className="px-4 py-3"><TypeBadge type={payment.payment_type} /></td>
                          <td className="px-4 py-3 text-gray-500">
                            {t(`payments.method.${payment.payment_method}`, payment.payment_method)}
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={payment.status} /></td>
                          <td className="px-4 py-3"><FlagBadge flag={payment.import_flag} /></td>
                          <td className="px-4 py-3 text-xs text-gray-400 font-mono">
                            {payment.reference_number || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Breakdown tables */}
      <BreakdownTables financial={financial} isLoading={financialLoading} />

      {/* Modals */}
      <BulkPaymentUploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onComplete={() => invalidateQueries.payments()}
      />

      <StudentPickerDialog
        open={studentPickerOpen}
        onClose={() => setStudentPickerOpen(false)}
        onSelect={handleStudentSelected}
      />

      {addPaymentStudent && (
        <AddPaymentDialog
          open={!!addPaymentStudent}
          onOpenChange={(open) => { if (!open) setAddPaymentStudent(null); }}
          student={addPaymentStudent}
        />
      )}

      {resolving && (
        <ResolvePaymentDialog
          payment={resolving}
          onClose={() => setResolving(null)}
        />
      )}
    </div>
  );
}
